<template lang="pug">
  .b-crm-funnel-form-ui
    ._header
      h2._title {{ createMode ? 'Создание воронки' : 'Настройка воронок' }}
      Btn._delete-funnel(v-if="!createMode" xs dark type="button" @click="$emit('deleteFunnel')") Удалить воронку
    ._field
      span._field-label Название воронки
      BaseInput(:value="funnelName" @input="$emit('updateName', $event)")
      ._save-row(v-if="showSaveName")
        Btn._save-name(
          sm blue
          type="button"
          :disabled="!funnelName.trim()"
          @click="$emit('saveName')"
        ) Сохранить
        //- Только при создании: сервер заполняет новую воронку наборами по
        //- умолчанию (см. withDefaults), а у существующей менять уже нечего
        BaseCheckbox._defaults(
          v-if="createMode"
          :value="withDefaults"
          label="Создать этапы, причины отказа и типы услуг"
          @input="$emit('updateWithDefaults', $event)"
          )
    //- содержимое вкладок принадлежит уже созданной воронке — до первого
    //- сохранения его некуда привязывать, поэтому в режиме создания форма
    //- состоит из одного поля названия
    template(v-if="!createMode")
      BaseTabs2._tabs(:items="tabItems" v-model="activeTab")
      p._description {{ tab.description }}
      //- dragenter.prevent has no handler of its own — onListDragOver (bound
      //- to dragover) does all the work — but some browsers only keep
      //- treating an element as a valid drop target if dragenter's default
      //- was ALSO prevented somewhere along the way, not just dragover's
      ._list(@dragenter.prevent @dragover.prevent="onListDragOver" @drop.prevent="onListDrop")
        ._row(
          v-for="item in activeItems"
          :key="item.id"
          :class="{ drag: item.id === draggedId }"
          :style="rowStyle(item)"
          draggable="true"
          @dragstart="onRowDragStart(item, $event)"
          @dragend="onRowDragEnd"
        )
          //- wrapped in a bigger hit box than the icon itself — 8x13px is too
          //- small a target to reliably grab for a drag gesture
          span._handle(@pointerdown="onHandlePointerDown")
            Icon(name="drag-handle")
          //- always an <input type="color"> (never a swapped-in <span>) so a
          //- click opens the native picker anchored to a node that's already
          //- laid out, instead of one that just got mounted this same tick
          input._dot.funnel-form-dot(
            v-if="tab.hasColor"
            type="color"
            :value="dotColor(item)"
            @click="onDotClick(item)"
            @input="onDotInput($event)"
          )
          template(v-if="editingId === item.id")
            input._input.funnel-form-input(v-model="editingName" @keyup.enter="saveEdit")
            RequestIndicator._save-indicator(v-if="saving" loading)
            Btn._save.funnel-form-save(v-else xs blue type="button" @click="saveEdit") Сохранить
          template(v-else)
            span._name(@click="startEdit(item)") {{ item.name }}
            ._actions
              button._action(type="button" title="Удалить" @click="deleteItem(item)")
                Icon(name="trash" pointer colored)
        ._row(v-if="editingId === 'new'")
          span._handle
            Icon(name="drag-handle")
          input._dot.funnel-form-dot(v-if="tab.hasColor" type="color" v-model="editingColor")
          input._input.funnel-form-input(v-model="editingName" :placeholder="tab.placeholder" @keyup.enter="saveEdit")
          RequestIndicator._save-indicator(v-if="saving" loading)
          Btn._save.funnel-form-save(v-else xs blue type="button" @click="saveEdit") Сохранить
      ._add-row
        Btn._add(sm green type="button" @click="startAdd") {{ tab.addLabel }}
        //- остаётся в разметке и после сохранения: индикатор сам показывает
        //- «Сохранено» и гаснет по своему таймеру
        RequestIndicator._list-indicator(v-if="listSaving !== null" :loading="listSaving")
      template(v-if="activeTab === 'stages'")
        p._subheading Завершающие этапы
        p._description {{ closingStagesDescription }}
        //- Названия и цвета берём из CLOSING_COLUMNS, а не пишем здесь ещё
        //- раз: это те же самые колонки, что и на доске
        ._list
          ._row.-static(v-for="column in closingColumns" :key="column.id")
            span._handle
              Icon(name="drag-handle")
            span._dot(:style="{ backgroundColor: column.color }")
            span._name {{ column.name }}
</template>

<script>
import { CLOSING_COLUMNS } from '@/model/crm';

import BaseTabs2 from '@/components/BaseTabs2.vue';
import BaseInput from '@/components/BaseInput.vue';
import BaseCheckbox from '@/components/BaseCheckbox.vue';
import enableTouchDrag from '@/utils/touchDrag';

// перебирается по кругу при создании нового этапа
const COLOR_PALETTE = [
  '#5AC169', '#FFC531', '#FF8D4D', '#33A1FB', '#19DAC6',
  '#7FA9FA', '#99A3C5', '#95F0F0', '#AA3E86', '#E21313',
];

/**
 * Вкладки формы: три одинаково устроенных списка, различающихся только
 * подписями, источником элементов и именами событий наружу. Держим это
 * таблицей, а не ветвлениями по activeTab: последние были двухместными
 * тернарниками и с третьей вкладкой разъехались бы в восьми местах сразу.
 *
 * itemsProp / savingProp — имена пропов, откуда брать элементы и состояние
 * операции над списком; hasColor есть только у этапов.
 */
const TABS = [
  {
    name: 'stages',
    label: 'Этапы',
    itemsProp: 'stages',
    savingProp: 'stagesSaving',
    hasColor: true,
    addLabel: 'Новый этап',
    placeholder: 'Название этапа',
    description: 'Настройте все этапы воронки, по которым продвигаются заявки. При создании заявка появится в начале воронки',
    events: {
      add: 'addStage', rename: 'renameStage', delete: 'deleteStage', reorder: 'reorderStages',
    },
  },
  {
    name: 'reasons',
    label: 'Причины отказа',
    itemsProp: 'rejectionReasons',
    savingProp: 'reasonsSaving',
    hasColor: false,
    addLabel: 'Новая причина отказа',
    placeholder: 'Название причины отказа',
    description: 'Сотрудники должны указать причину отказа для каждой неуспешной заявки. Эта информация поможет вам определить слабые места в воронке продаж',
    events: {
      add: 'addReason', rename: 'renameReason', delete: 'deleteReason', reorder: 'reorderReasons',
    },
  },
  {
    name: 'serviceTypes',
    label: 'Типы услуг',
    itemsProp: 'serviceTypes',
    savingProp: 'serviceTypesSaving',
    hasColor: false,
    addLabel: 'Новый тип услуги',
    placeholder: 'Название типа услуги',
    description: 'Перечислите услуги, с которыми приходят клиенты. Тип услуги указывают в заявке, а на доске по нему можно отфильтровать заявки',
    events: {
      add: 'addServiceType', rename: 'renameServiceType', delete: 'deleteServiceType', reorder: 'reorderServiceTypes',
    },
  },
];

export default {
  name: 'CrmFunnelFormUi',
  components: { BaseTabs2, BaseInput, BaseCheckbox },
  props: {
    stages: {
      type: Array,
      required: true,
    },
    rejectionReasons: {
      type: Array,
      required: true,
    },
    serviceTypes: {
      type: Array,
      required: true,
    },
    funnelName: {
      type: String,
      required: true,
    },
    // наполнить новую воронку наборами по умолчанию; только при создании
    withDefaults: Boolean,
    // воронка ещё не создана: доступно только поле названия
    createMode: {
      type: Boolean,
      default: false,
    },
    // показать кнопку «Сохранить» под названием — в режиме создания всегда,
    // в режиме редактирования только пока название отличается от сохранённого
    showSaveName: {
      type: Boolean,
      default: false,
    },
    // состояние операции над списком целиком (сортировка, удаление):
    // null — показывать нечего, true — запрос идёт, false — сохранено.
    // Добавление и переименование сюда не входят: там состояние показывает
    // индикатор в самой строке
    stagesSaving: {
      type: Boolean,
      default: null,
    },
    reasonsSaving: {
      type: Boolean,
      default: null,
    },
    serviceTypesSaving: {
      type: Boolean,
      default: null,
    },
  },
  data() {
    return {
      closingColumns: CLOSING_COLUMNS,
      activeTab: 'stages',
      // 'new' while adding a row, an item's id while renaming it, otherwise null
      editingId: null,
      editingName: '',
      editingColor: '',
      // идёт сохранение строки: блокирует повторный сабмит и не даёт
      // закрыть редактирование раньше ответа сервера
      saving: false,
      // drag-to-reorder state (stages/reasons rows only — the closing
      // stages are static and never take part in this)
      draggedId: null,
      // the index the dragged item would land at if dropped right now
      dragOverIndex: null,
      dragFromHandle: false,
      // the dragged row's own rendered height, measured at drag start —
      // used to slide rows out of the way by exactly one row's worth
      dragRowHeight: 0,
      // the dragged row's own original vertical center, measured at drag
      // start — dragOverIndex is computed from how far the cursor has
      // travelled from this point, in row-height units (see
      // onListDragOver), rather than against other rows' positions
      dragStartCenterY: 0,
      tabItems: TABS.map(({ name, label }) => ({ name, label })),
      closingStagesDescription: 'При неуспешном закрытии заявки сотрудник должен указать причину отказа. Эта информация поможет понять, что стоит изменить, чтобы увеличить вероятность успешного закрытия заявки',
    };
  },
  watch: {
    editingId(value) {
      if (value) {
        document.addEventListener('mousedown', this.handleOutsideClick);
      } else {
        document.removeEventListener('mousedown', this.handleOutsideClick);
      }
    },
  },
  mounted() {
    // на телефоне строки переставляют пальцем: браузер сам drag-события из
    // касаний не порождает, вся разметка ниже рассчитана на мышь
    this.disableTouchDrag = enableTouchDrag(this.$el);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleOutsideClick);
    this.disableTouchDrag();
  },
  computed: {
    /** Конфиг активной вкладки — через него идут все различия между списками. */
    tab() {
      return TABS.find(item => item.name === this.activeTab) ?? TABS[0];
    },
    activeItems() {
      return this[this.tab.itemsProp];
    },
    nextColor() {
      return COLOR_PALETTE[this.activeItems.length % COLOR_PALETTE.length];
    },
    listSaving() {
      return this[this.tab.savingProp];
    },
  },
  methods: {
    startAdd() {
      this.editingId = 'new';
      this.editingName = '';
      this.editingColor = this.nextColor;
      this.focusInput();
    },
    startEdit(item) {
      this.editingId = item.id;
      this.editingName = item.name;
      this.editingColor = item.color;
      this.focusInput();
    },
    // the dot bound to the row being edited shows the in-progress color;
    // every other row's dot just shows its own item's saved color
    dotColor(item) {
      return this.editingId === item.id ? this.editingColor : item.color;
    },
    // clicking a swatch on a row that isn't already being edited switches
    // editing to it (without stealing focus from the color picker the
    // browser is about to open on this same click, so the name field is
    // deliberately not focused here — unlike startEdit)
    onDotClick(item) {
      if (this.editingId === item.id) return;
      this.editingId = item.id;
      this.editingName = item.name;
      this.editingColor = item.color;
    },
    onDotInput(e) {
      this.editingColor = e.target.value;
    },
    focusInput() {
      this.$nextTick(() => {
        // .funnel-form-input — намеренный хук-класс на поле ввода строки:
        // БЭМ-имя генерируется из имени блока и переживёт не всякое
        // переименование, а по хук-классу видно, что на него завязан код
        this.$el.querySelector('.funnel-form-input')?.focus();
      });
    },
    cancelEdit() {
      this.editingId = null;
      this.editingName = '';
      this.editingColor = '';
      this.saving = false;
    },
    // Строка редактирования снимается не сразу, а только когда родитель
    // сообщит, что запрос прошёл (payload.done) — иначе новый элемент
    // успевает исчезнуть и появиться заново, пока идёт POST. При неудачном
    // сохранении строка остаётся на месте вместе с введённым текстом
    saveEdit() {
      const name = this.editingName.trim();
      if (!name || this.saving) return;

      this.saving = true;
      const done = (saved) => {
        this.saving = false;
        if (saved) this.cancelEdit();
      };

      // цвет есть только у этапов — остальным спискам он и не нужен, и
      // сервером не принимается
      const payload = { name, done };
      if (this.tab.hasColor) payload.color = this.editingColor;

      if (this.editingId === 'new') {
        this.$emit(this.tab.events.add, payload);
      } else {
        this.$emit(this.tab.events.rename, { ...payload, id: this.editingId });
      }
    },
    deleteItem(item) {
      this.$emit(this.tab.events.delete, item.id);
    },
    // cancels editing on any click that isn't the editing input itself, the
    // "Сохранить" button, or a color-picker square (every row's dot matches
    // this — clicking another row's dot switches editing to it via
    // onDotClick instead of cancelling, so this stays a no-op there too)
    handleOutsideClick(e) {
      if (this.saving) return;
      // хук-классы, а не БЭМ-имена: последние генерируются из имени блока и
      // молча перестанут совпадать при его переименовании. .funnel-form-dot
      // висит только на input[type="color"], поэтому уточнять тип не нужно —
      // у статичных завершающих этапов кружок это span._dot без этого класса
      const safeSelector = '.funnel-form-input, .funnel-form-save, .funnel-form-dot';
      if (e.target.closest(safeSelector)) return;
      this.cancelEdit();
    },
    // the row itself is what's draggable="true" (dragging a single native
    // element gives a cleaner drag image than the handle icon alone), but
    // only a drag that actually started on the handle should count — this
    // flag, set by the handle's own pointerdown, gates onRowDragStart.
    // pointerdown rather than mousedown so a finger sets it too: a touch
    // fires no mousedown before the drag starts (see utils/touchDrag)
    onHandlePointerDown() {
      this.dragFromHandle = true;
    },
    onRowDragStart(item, e) {
      if (!this.dragFromHandle) {
        e.preventDefault();
        return;
      }
      this.dragFromHandle = false;
      this.draggedId = item.id;
      const rect = e.currentTarget.getBoundingClientRect();
      this.dragRowHeight = rect.height;
      this.dragStartCenterY = rect.top + rect.height / 2;

      e.dataTransfer.effectAllowed = 'move';
      // Firefox requires setData to be called for the drag to start at all
      e.dataTransfer.setData('text/plain', String(item.id));
    },
    // fires continuously while dragging anywhere over the list — figures
    // out the drop index from how many row-heights the cursor has moved
    // from the DRAGGED ROW'S OWN starting center, rounded to the nearest
    // whole row. This uses the exact same reference frame as rowStyle's
    // preview offset ((toIndex - fromIndex) * dragRowHeight), so the
    // logical swap point can never drift out of sync with what's visually
    // displayed — which is what happened comparing against other rows'
    // frozen original positions instead: those rows visually slide away
    // from that position after an earlier swap in the same drag, so the
    // threshold silently stopped matching what was on screen the further
    // the drag had already travelled. Must NOT touch activeItems/the
    // stages array here: doing that mid-drag would move the dragged row's
    // actual DOM node, and browsers abort a native drag the instant its
    // source element is detached/moved, which is exactly what broke this
    // the first time
    onListDragOver(e) {
      if (this.draggedId === null || !this.dragRowHeight) return;

      const fromIndex = this.activeItems.findIndex(i => i.id === this.draggedId);
      if (fromIndex === -1) return;

      const stepsRaw = (e.clientY - this.dragStartCenterY) / this.dragRowHeight;
      // round the magnitude, not the signed value — Math.round alone
      // breaks ties towards +Infinity, which would make -0.5 round to 0
      // instead of -1, reintroducing a one-row asymmetry right at the
      // halfway point
      const steps = Math.sign(stepsRaw) * Math.round(Math.abs(stepsRaw));

      const maxIndex = this.activeItems.length - 1;
      this.dragOverIndex = Math.max(0, Math.min(maxIndex, fromIndex + steps));
    },
    // visually slides rows out of the way to preview exactly where the
    // dragged item would land, purely via a CSS transform on top of each
    // row's normal (untouched) DOM position — see onListDragOver for why
    // the actual list order can't change until drop
    rowStyle(item) {
      if (this.draggedId === null || this.dragOverIndex === null) return null;

      const items = this.activeItems;
      const fromIndex = items.findIndex(i => i.id === this.draggedId);
      const toIndex = this.dragOverIndex;
      if (fromIndex === -1 || fromIndex === toIndex) return null;

      if (item.id === this.draggedId) {
        const offset = (toIndex - fromIndex) * this.dragRowHeight;
        return { transform: `translateY(${offset}px)` };
      }

      const index = items.findIndex(i => i.id === item.id);
      if (fromIndex < toIndex && index > fromIndex && index <= toIndex) {
        return { transform: `translateY(${-this.dragRowHeight}px)` };
      }
      if (fromIndex > toIndex && index >= toIndex && index < fromIndex) {
        return { transform: `translateY(${this.dragRowHeight}px)` };
      }
      return null;
    },
    onListDrop() {
      if (this.draggedId === null || this.dragOverIndex === null) {
        this.onRowDragEnd();
        return;
      }

      const orderedIds = this.activeItems.map(i => i.id);
      const fromIndex = orderedIds.indexOf(this.draggedId);
      orderedIds.splice(fromIndex, 1);
      orderedIds.splice(this.dragOverIndex, 0, this.draggedId);

      this.$emit(this.tab.events.reorder, orderedIds);
      this.onRowDragEnd();
    },
    onRowDragEnd() {
      this.draggedId = null;
      this.dragOverIndex = null;
      this.dragFromHandle = false;
      this.dragRowHeight = 0;
      this.dragStartCenterY = 0;
    },
  },
};
</script>

<style scoped lang="scss">
.crm-funnel-form-ui {
  width: 462px;
  max-width: 100%;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 20px;
  }

  &__title {
    font-weight: 700;
    font-size: 18px;
    color: var(--color-dark-gray);
  }

  &__field {
    margin-bottom: 20px;
  }

  &__field-label {
    display: block;
    font-size: 12px;
    color: var(--color-middle-gray);
    margin-bottom: 8px;
  }

  &__save-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
    margin-top: 12px;
  }

  &__save-name {
    flex-shrink: 0;
  }

  &__defaults {
    // подпись длинная и при узком попапе переносится на вторую строку —
    // пусть занимает остаток строки, а не распирает её
    min-width: 0;
    font-size: 12px;
  }

  &__tabs {
    margin-bottom: 16px;
  }

  &__description {
    font-size: 12px;
    line-height: 1.4;
    color: var(--color-middle-gray);
    margin-bottom: 16px;
  }

  &__subheading {
    font-weight: 500;
    font-size: 14px;
    color: var(--color-dark-gray);
    margin: 24px 0 16px;
  }

  &__list {
    display: flex;
    flex-direction: column;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 40px;
    padding: 0 4px;
    border-bottom: 1px solid var(--color-purple);

    // the row currently being dragged (closing-stage rows never get this,
    // since they aren't draggable in the first place) — the sliding
    // rowStyle() transform is what shows the drop position, so this is
    // just the "picked up" look. Rows snap straight to their preview
    // position with no transition: animating it raced against the DOM
    // reorder that commits on drop and produced a jump-then-animate
    // glitch, so this is intentionally instant rather than smoothed
    &.drag {
      position: relative;
      z-index: 1;
      opacity: .5;
    }
  }

  &__handle {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    // the icon itself stays 8x13px (unchanged visually), but the
    // clickable/draggable box around it is enlarged — the icon alone is
    // too small a target to reliably grab
    width: 20px;
    height: 20px;
    margin: 0 -6px;
    cursor: grab;

    svg {
      display: block;
      width: 8px;
      height: 13px;
    }
  }

  &__dot {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border-radius: 5px;
    cursor: pointer;

    // the color-picker version, shown only on the row being edited
    &[type="color"] {
      -webkit-appearance: none;
      appearance: none;
      border: none;
      padding: 0;
      background: none;
      cursor: pointer;

      &::-webkit-color-swatch-wrapper {
        padding: 0;
        border-radius: 5px;
      }

      &::-webkit-color-swatch {
        border: none;
        border-radius: 5px;
      }
    }
  }

  // the closing stages (Сделка завершена / Отказ) are static — their dot
  // isn't editable, so it shouldn't look clickable
  &__row--static &__dot {
    cursor: default;
  }

  &__name {
    flex-grow: 1;
    // строка фиксированной высоты, поэтому длинное название не переносим, а
    // обрезаем многоточием: min-width нужен, чтобы флекс-элемент вообще дал
    // себя сжать, иначе он распирает строку по своему содержимому
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    height: 32px;
    line-height: 32px;
    font-size: 12px;
    color: var(--color-dark-gray);
    cursor: pointer;
  }

  &__input {
    flex-grow: 1;
    min-width: 0;
    height: 32px;
    line-height: 32px;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: none;
    font-size: 12px;
    font-weight: 500;
    color: var(--color-dark-gray);
    outline: none;

    &::placeholder {
      color: var(--color-middle-gray);
    }
  }

  &__actions {
    flex-shrink: 0;
    display: flex;
    gap: 4px;
  }

  &__action {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: none;
    border: none;
    padding: 0;
    color: var(--color-middle-gray);
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.7;
    }

    // edit3.svg and trash.svg aren't the same intrinsic aspect ratio (15x15
    // vs 12x15) — cap height only so each keeps its own proportions instead
    // of one getting squashed to fit a forced square
    svg {
      width: auto;
      height: 16px;
    }
  }

  &__save {
    flex-shrink: 0;
  }

  &__save-indicator {
    flex-shrink: 0;
  }

  &__add-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 16px;
    justify-content: space-between;
  }

  &__add {
    flex-shrink: 0;
  }
}
</style>
